import React, { useState, useEffect, useMemo } from 'react';
import { getExecutiveEditors, saveLocalEditors } from '../services/firebaseService';
import ImageUploadField from './ImageUploadField';
import {
  FaUserPlus,
  FaEdit,
  FaTrash,
  FaArrowUp,
  FaArrowDown,
  FaSearch,
  FaTimes,
  FaStar,
  FaEnvelope,
  FaPhone,
  FaUniversity,
  FaGraduationCap,
  FaBriefcase,
  FaMapMarkerAlt,
  FaImage,
  FaCheckCircle,
  FaExclamationCircle,
  FaTags,
  FaLayerGroup,
  FaList,
  FaPlus
} from 'react-icons/fa';


const INITIAL_ROLE_PRESETS = [
  'Editor-in-Chief',
  'Editorial Board',
  'Editorial Board Member',
  'Associate Editor',
  'Section Editor',
  'Advisory Board Member',
  'Managing Editor'
];

const emptyEditorForm = {
  name: '',
  designation: 'Editorial Board Member',
  memberDesignation: '',
  address: '',
  qualification: '',
  institution: '',
  email: '',
  contactNumber: '',
  profileLink: '',
  sequenceNumber: '',
  img: '',
  social: [{ icon: '', url: '' }]
};

const ExecutiveEditorsAdmin = () => {
  const [editors, setEditors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // View Mode: 'grouped' | 'list'
  const [viewMode, setViewMode] = useState('grouped');

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');

  // Custom Categories
  const [customCategories, setCustomCategories] = useState([]);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [newCatInput, setNewCatInput] = useState('');
  const [editingCategoryName, setEditingCategoryName] = useState(null);
  const [renamedCategoryInput, setRenamedCategoryInput] = useState('');

  // Member Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' | 'edit'
  const [editingIndex, setEditingIndex] = useState(null);
  const [form, setForm] = useState(emptyEditorForm);
  const [showInlineCatCreator, setShowInlineCatCreator] = useState(false);
  const [inlineCatInput, setInlineCatInput] = useState('');

  useEffect(() => {
    fetchEditors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEditors = async () => {
    setLoading(true);
    try {
      const local = await getExecutiveEditors();
      setEditors(sortEditorsWithDheerajFirst(local || []));
      setError('');
    } catch (err) {
      console.error(err);
      setError('Failed to fetch executive editors from database.');
    }
    setLoading(false);
  };

  // Helper to ensure Dr. Dheeraj is ALWAYS at index 0 / top
  const sortEditorsWithDheerajFirst = (list) => {
    return [...list].sort((a, b) => {
      const isDheerajA = (a.name || '').toLowerCase().includes('dheeraj');
      const isDheerajB = (b.name || '').toLowerCase().includes('dheeraj');
      if (isDheerajA) return -1;
      if (isDheerajB) return 1;

      const seqA = parseInt(a.sequenceNumber, 10);
      const seqB = parseInt(b.sequenceNumber, 10);
      if (!isNaN(seqA) && !isNaN(seqB)) return seqA - seqB;
      return 0;
    });
  };

  // Compute all available categories (presets + custom + in-use)
  const allCategories = useMemo(() => {
    const set = new Set(INITIAL_ROLE_PRESETS);
    customCategories.forEach((c) => set.add(c.trim()));
    editors.forEach((e) => {
      const d = (e.designation || '').trim();
      if (d) set.add(d);
    });
    return Array.from(set);
  }, [editors, customCategories]);

  // Count members per category
  const categoryCounts = useMemo(() => {
    const map = {};
    allCategories.forEach((c) => {
      map[c] = 0;
    });
    editors.forEach((e) => {
      const d = (e.designation || 'Editorial Board Member').trim();
      map[d] = (map[d] || 0) + 1;
    });
    return map;
  }, [allCategories, editors]);

  const persistEditors = async (newEditors, successMsg) => {
    setSaving(true);
    setError('');
    try {
      const sorted = sortEditorsWithDheerajFirst(newEditors);
      // Written into public/data/editors.json
      await saveLocalEditors(sorted);

      setEditors(sorted);
      setSuccess(successMsg || 'Saved! It is live now.');
      setTimeout(() => setSuccess(''), 3500);
      return true;
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to save changes. Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Open Member Modal for Add
  const handleOpenAdd = (defaultCategory = null) => {
    setForm({
      ...emptyEditorForm,
      designation: defaultCategory || 'Editorial Board Member',
      sequenceNumber: (editors.length + 1).toString()
    });
    setModalMode('add');
    setEditingIndex(null);
    setShowInlineCatCreator(false);
    setIsModalOpen(true);
  };

  // Open Member Modal for Edit
  const handleOpenEdit = (editor, originalIndex) => {
    setForm({
      name: editor.name || '',
      designation: editor.designation || 'Editorial Board Member',
      memberDesignation: editor.memberDesignation || editor.designationTitle || editor.position || '',
      address: editor.address || editor.institution || '',
      qualification: editor.qualification || '',
      institution: editor.institution || '',
      email: editor.email || '',
      contactNumber: editor.contactNumber || '',
      profileLink: editor.profileLink || '',
      sequenceNumber: editor.sequenceNumber || (originalIndex + 1).toString(),
      img: editor.img || '',
      social: Array.isArray(editor.social) && editor.social.length > 0
        ? editor.social
        : [{ icon: '', url: '' }]
    });
    setModalMode('edit');
    setEditingIndex(originalIndex);
    setShowInlineCatCreator(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setForm(emptyEditorForm);
    setEditingIndex(null);
    setShowInlineCatCreator(false);
  };

  // Save Member Modal Form
  const handleSaveModal = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      alert('Please enter member name.');
      return;
    }

    const payload = {
      ...form,
      // Keep institution and address synced for complete compatibility
      institution: form.institution || form.address || '',
      address: form.address || form.institution || ''
    };

    let updatedList;
    if (modalMode === 'edit' && editingIndex !== null) {
      updatedList = editors.map((item, idx) => (idx === editingIndex ? payload : item));
    } else {
      updatedList = [...editors, payload];
    }

    const ok = await persistEditors(
      updatedList,
      modalMode === 'edit' ? `Updated ${form.name}` : `Added ${form.name}`
    );
    if (ok) {
      handleCloseModal();
    }
  };

  // Move Up (Reorder)
  const handleMoveUp = async (index) => {
    if (index <= 1) return; // Cannot move above Dr. Dheeraj (index 0)
    const updated = [...editors];
    const temp = updated[index];
    updated[index] = updated[index - 1];
    updated[index - 1] = temp;

    updated.forEach((ed, i) => {
      ed.sequenceNumber = (i + 1).toString();
    });

    await persistEditors(updated, `Moved ${temp.name || 'member'} up`);
  };

  // Move Down (Reorder)
  const handleMoveDown = async (index) => {
    if (index === 0) return; // Dr. Dheeraj stays at top
    if (index >= editors.length - 1) return;
    const updated = [...editors];
    const temp = updated[index];
    updated[index] = updated[index + 1];
    updated[index + 1] = temp;

    updated.forEach((ed, i) => {
      ed.sequenceNumber = (i + 1).toString();
    });

    await persistEditors(updated, `Moved ${temp.name || 'member'} down`);
  };

  // Delete Member
  const handleDelete = async (index) => {
    const target = editors[index];
    if ((target.name || '').toLowerCase().includes('dheeraj')) {
      alert('Dr. Dheeraj Pandey is the designated Editor-in-Chief and cannot be deleted.');
      return;
    }

    if (!window.confirm(`Are you sure you want to remove "${target.name || 'this member'}" from the Editorial Board?`)) {
      return;
    }

    const updated = editors.filter((_, idx) => idx !== index);
    updated.forEach((ed, i) => {
      ed.sequenceNumber = (i + 1).toString();
    });

    await persistEditors(updated, `Removed ${target.name || 'member'}`);
  };

  // ================= CATEGORY MANAGEMENT HANDLERS =================
  const handleCreateCategory = (catName) => {
    const trimmed = (catName || '').trim();
    if (!trimmed) return;
    if (!allCategories.includes(trimmed)) {
      setCustomCategories((prev) => [...prev, trimmed]);
    }
    setNewCatInput('');
    setSuccess(`Category "${trimmed}" created! It can now be assigned to board members.`);
    setTimeout(() => setSuccess(''), 3500);
  };

  const handleInlineAddCategory = (catName) => {
    const trimmed = (catName || '').trim();
    if (!trimmed) return;
    if (!allCategories.includes(trimmed)) {
      setCustomCategories((prev) => [...prev, trimmed]);
    }
    setForm((prev) => ({ ...prev, designation: trimmed }));
    setInlineCatInput('');
    setShowInlineCatCreator(false);
  };

  const handleRenameCategory = async (oldName, newName) => {
    const trimmedNew = (newName || '').trim();
    if (!trimmedNew || trimmedNew === oldName) {
      setEditingCategoryName(null);
      return;
    }

    // Update all editors in database that had the old category name
    const updated = editors.map((ed) => {
      if ((ed.designation || '').trim() === oldName) {
        return { ...ed, designation: trimmedNew };
      }
      return ed;
    });

    setCustomCategories((prev) =>
      prev.map((c) => (c === oldName ? trimmedNew : c))
    );

    setEditingCategoryName(null);
    setRenamedCategoryInput('');

    await persistEditors(
      updated,
      `Renamed category "${oldName}" to "${trimmedNew}" across all member profiles!`
    );
  };

  // Social link helpers in modal
  const handleSocialChange = (idx, field, value) => {
    const updated = form.social.map((s, i) => (i === idx ? { ...s, [field]: value } : s));
    setForm({ ...form, social: updated });
  };

  const addSocial = () => {
    setForm({ ...form, social: [...form.social, { icon: '', url: '' }] });
  };

  const removeSocial = (idx) => {
    setForm({ ...form, social: form.social.filter((_, i) => i !== idx) });
  };

  // Filtered list
  const filteredEditors = useMemo(() => {
    return editors.filter((ed) => {
      if (roleFilter !== 'ALL') {
        const d = (ed.designation || '').trim();
        if (d !== roleFilter) return false;
      }

      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        (ed.name && ed.name.toLowerCase().includes(q)) ||
        (ed.designation && ed.designation.toLowerCase().includes(q)) ||
        (ed.institution && ed.institution.toLowerCase().includes(q)) ||
        (ed.qualification && ed.qualification.toLowerCase().includes(q)) ||
        (ed.email && ed.email.toLowerCase().includes(q))
      );
    });
  }, [editors, roleFilter, searchQuery]);

  // Grouped members for 'grouped' view
  const groupedSections = useMemo(() => {
    const groups = [];
    const catMap = new Map();

    filteredEditors.forEach((editor) => {
      const cat = (editor.designation || 'Editorial Board Member').trim();
      if (!catMap.has(cat)) {
        catMap.set(cat, []);
      }
      catMap.get(cat).push(editor);
    });

    // Editor-in-Chief first
    allCategories.forEach((cat) => {
      if (catMap.has(cat)) {
        groups.push({
          title: cat,
          members: catMap.get(cat)
        });
        catMap.delete(cat);
      }
    });

    // Any remaining
    catMap.forEach((members, title) => {
      groups.push({ title, members });
    });

    return groups;
  }, [filteredEditors, allCategories]);

  // Reusable Member Card Component
  const renderMemberCard = (editor) => {
    const originalIndex = editors.findIndex((e) => e === editor);
    const isDheeraj = (editor.name || '').toLowerCase().includes('dheeraj');
    const isFirst = originalIndex <= 1; // 1 because 0 is Dheeraj
    const isLast = originalIndex >= editors.length - 1;

    return (
      <div
        key={editor.name + originalIndex}
        className={`bg-white rounded-2xl p-4 sm:p-5 shadow-xs border transition-all ${
          isDheeraj
            ? 'border-blue-300 bg-blue-50/40 ring-1 ring-blue-300'
            : 'border-gray-200 hover:border-blue-200 hover:shadow-sm'
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          {/* Avatar & Details */}
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className="relative flex-shrink-0">
              <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white shadow-xs bg-gray-100 flex items-center justify-center">
                {editor.img ? (
                  <img
                    src={editor.img}
                    alt={editor.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/assets/img/default-doctor.svg';
                    }}
                  />
                ) : (
                  <span className="text-base font-bold text-[#03478E]">
                    {(editor.name || 'D').slice(0, 2).toUpperCase()}
                  </span>
                )}
              </div>
              {isDheeraj && (
                <span
                  title="Pinned at Top"
                  className="absolute -top-1 -right-1 bg-amber-400 text-amber-950 p-1 rounded-full shadow-xs text-[10px]"
                >
                  <FaStar />
                </span>
              )}
            </div>

            <div className="space-y-1 min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                  #{originalIndex + 1}
                </span>
                <h3 className="text-base font-bold text-gray-900 leading-snug">
                  {editor.name}
                </h3>
                {isDheeraj && (
                  <span className="text-[10px] font-extrabold uppercase tracking-wider bg-[#03478E] text-white px-2.5 py-0.5 rounded-full">
                    Editor-in-Chief (Pinned)
                  </span>
                )}
                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-50 text-[#03478E] border border-blue-100">
                  {editor.designation || 'Board Member'}
                </span>
              </div>

              {(editor.memberDesignation || editor.designationTitle) && (
                <p className="text-xs text-gray-800 font-medium flex items-start gap-1.5 leading-snug">
                  <FaBriefcase className="text-[#03478E] text-[11px] flex-shrink-0 mt-0.5" />
                  <span>{editor.memberDesignation || editor.designationTitle}</span>
                </p>
              )}

              {editor.qualification && (
                <p className="text-xs text-gray-600 flex items-start gap-1.5">
                  <FaGraduationCap className="text-gray-400 text-[11px] flex-shrink-0 mt-0.5" />
                  <span>{editor.qualification}</span>
                </p>
              )}

              {(editor.address || editor.institution) && (
                <p className="text-xs text-gray-600 flex items-start gap-1.5 leading-relaxed">
                  <FaMapMarkerAlt className="text-red-500 text-[11px] flex-shrink-0 mt-0.5" />
                  <span>{editor.address || editor.institution}</span>
                </p>
              )}

              {editor.institution && editor.address && editor.institution !== editor.address && (
                <p className="text-xs text-gray-500 italic flex items-center gap-1.5">
                  <FaUniversity className="text-gray-400 text-[11px] flex-shrink-0" />
                  <span>{editor.institution}</span>
                </p>
              )}

              <div className="flex items-center gap-4 text-[11px] text-gray-500 pt-1 flex-wrap">
                {editor.email && (
                  <span className="flex items-center gap-1">
                    <FaEnvelope className="text-gray-400" />
                    {editor.email}
                  </span>
                )}
                {editor.contactNumber && (
                  <span className="flex items-center gap-1">
                    <FaPhone className="text-gray-400" />
                    {editor.contactNumber}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0 border-t sm:border-t-0 pt-3 sm:pt-0 justify-end">
            {!isDheeraj && (
              <div className="flex items-center bg-gray-50 rounded-xl p-1 border border-gray-200">
                <button
                  type="button"
                  disabled={isFirst || saving}
                  onClick={() => handleMoveUp(originalIndex)}
                  title="Move Up"
                  className="p-1.5 text-gray-600 hover:text-[#03478E] hover:bg-white rounded-lg disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <FaArrowUp className="text-xs" />
                </button>
                <button
                  type="button"
                  disabled={isLast || saving}
                  onClick={() => handleMoveDown(originalIndex)}
                  title="Move Down"
                  className="p-1.5 text-gray-600 hover:text-[#03478E] hover:bg-white rounded-lg disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <FaArrowDown className="text-xs" />
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={() => handleOpenEdit(editor, originalIndex)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-[#03478E] text-[#03478E] hover:text-white rounded-xl text-xs font-bold transition shadow-2xs"
            >
              <FaEdit className="text-xs" />
              <span>Edit</span>
            </button>

            {!isDheeraj && (
              <button
                type="button"
                onClick={() => handleDelete(originalIndex)}
                title="Delete Member"
                className="p-2 text-red-500 hover:bg-red-50 hover:text-red-700 rounded-xl text-xs transition"
              >
                <FaTrash className="text-xs" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Top Banner & Control Bar */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-serif font-bold text-[#0B3B60]">
              Editorial Board Members
            </h2>
            <span className="text-xs bg-blue-100 text-[#03478E] font-bold px-2.5 py-0.5 rounded-full">
              {editors.length} Total
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Manage members and categories. Categories automatically group members with header banners on the public page.
          </p>
        </div>

        {/* Action Buttons: Add Category & Add Member */}
        <div className="flex items-center gap-2.5 flex-wrap">
          <button
            type="button"
            onClick={() => setIsCategoryModalOpen(true)}
            className="inline-flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2.5 rounded-xl text-xs font-bold shadow-2xs transition"
          >
            <FaTags className="text-sm text-[#03478E]" />
            <span>Manage Categories</span>
          </button>

          <button
            type="button"
            onClick={() => handleOpenAdd()}
            className="inline-flex items-center gap-2 bg-[#03478E] hover:bg-[#023366] text-white px-5 py-2.5 rounded-xl text-xs font-bold shadow-sm transition"
          >
            <FaUserPlus className="text-sm" />
            <span>Add New Member</span>
          </button>
        </div>
      </div>

      {/* Notifications */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-xs flex items-center gap-2">
          <FaExclamationCircle className="text-base flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-xs flex items-center gap-2">
          <FaCheckCircle className="text-base flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Filter and View Switcher Toolbar */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-3">
        {/* Dynamic Category Filter Chips */}
        <div className="flex flex-wrap items-center gap-1.5 flex-1">
          <button
            type="button"
            onClick={() => setRoleFilter('ALL')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              roleFilter === 'ALL'
                ? 'bg-[#03478E] text-white shadow-xs'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All ({editors.length})
          </button>
          {allCategories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setRoleFilter(cat)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                roleFilter === cat
                  ? 'bg-[#03478E] text-white shadow-xs'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cat}
              <span className="ml-1 opacity-70 font-mono text-[10px]">
                ({categoryCounts[cat] || 0})
              </span>
            </button>
          ))}
        </div>

        {/* Right side: View Mode Toggle & Search */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* View Mode Toggle */}
          <div className="bg-gray-100 p-1 rounded-xl flex items-center gap-1">
            <button
              type="button"
              onClick={() => setViewMode('grouped')}
              title="Grouped by Category"
              className={`px-2.5 py-1.5 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 transition ${
                viewMode === 'grouped'
                  ? 'bg-white text-[#03478E] shadow-2xs'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <FaLayerGroup className="text-xs" />
              <span className="hidden sm:inline">Grouped</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              title="Flat List View"
              className={`px-2.5 py-1.5 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 transition ${
                viewMode === 'list'
                  ? 'bg-white text-[#03478E] shadow-2xs'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <FaList className="text-xs" />
              <span className="hidden sm:inline">Flat</span>
            </button>
          </div>

          {/* Search Box */}
          <div className="relative w-48 sm:w-56">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search members..."
              className="w-full text-xs pl-8 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30 focus:bg-white"
            />
            <FaSearch className="absolute left-2.5 top-2.5 text-gray-400 text-xs" />
          </div>
        </div>
      </div>

      {/* Main Directory Display */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 text-center text-gray-400 font-medium">
          Loading editorial board members...
        </div>
      ) : filteredEditors.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-gray-500">
          <p className="text-sm font-semibold">No board members found matching your search.</p>
          <p className="text-xs text-gray-400 mt-1">Try clearing filters or search query.</p>
        </div>
      ) : viewMode === 'grouped' ? (
        /* ================= GROUPED BY CATEGORY VIEW ================= */
        <div className="space-y-8">
          {groupedSections.map((sec) => (
            <div key={sec.title} className="space-y-3">
              {/* Category Group Header Banner */}
              <div className="bg-white rounded-2xl px-5 py-3 border-l-4 border-[#03478E] shadow-xs flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-[#03478E]">
                    {sec.title}
                  </h3>
                  <span className="text-[11px] font-bold text-gray-500 bg-gray-100 px-2.5 py-0.5 rounded-full">
                    {sec.members.length} {sec.members.length === 1 ? 'Member' : 'Members'}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => handleOpenAdd(sec.title)}
                  className="text-xs text-[#03478E] hover:text-[#023366] font-bold inline-flex items-center gap-1 hover:underline"
                >
                  <FaPlus className="text-[10px]" />
                  <span>Add to this Category</span>
                </button>
              </div>

              {/* Members in this Category */}
              <div className="space-y-3">
                {sec.members.map((editor) => renderMemberCard(editor))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ================= FLAT LIST VIEW ================= */
        <div className="space-y-3">
          {filteredEditors.map((editor) => renderMemberCard(editor))}
        </div>
      )}

      {/* ======================= MANAGE CATEGORIES MODAL ======================= */}
      {isCategoryModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-xl w-full shadow-2xl overflow-hidden border border-gray-100 my-8">
            <div className="bg-[#03478E] text-white px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-serif font-bold text-lg flex items-center gap-2">
                  <FaTags />
                  <span>Manage Board Categories</span>
                </h3>
                <p className="text-xs text-blue-100">
                  Create, rename, and review categories. Each category displays as its own section on the public site.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsCategoryModalOpen(false)}
                className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 text-sm"
              >
                <FaTimes />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Add New Category Box */}
              <div className="p-4 bg-blue-50/70 border border-blue-200 rounded-2xl space-y-2">
                <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                  Create New Category
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="e.g. International Advisory Board, Managing Editors..."
                    value={newCatInput}
                    onChange={(e) => setNewCatInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCreateCategory(newCatInput);
                      }
                    }}
                    className="flex-1 text-xs p-2.5 border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                  <button
                    type="button"
                    onClick={() => handleCreateCategory(newCatInput)}
                    className="px-4 py-2.5 bg-[#03478E] hover:bg-[#023366] text-white rounded-xl text-xs font-bold transition shadow-xs"
                  >
                    Add Category
                  </button>
                </div>
              </div>

              {/* Existing Categories List */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Active Categories ({allCategories.length})
                </h4>

                <div className="divide-y divide-gray-100 border border-gray-200 rounded-2xl overflow-hidden max-h-60 overflow-y-auto">
                  {allCategories.map((cat) => {
                    const count = categoryCounts[cat] || 0;
                    const isEditing = editingCategoryName === cat;

                    return (
                      <div
                        key={cat}
                        className="p-3 bg-white flex items-center justify-between gap-3 hover:bg-gray-50/80 transition"
                      >
                        {isEditing ? (
                          <div className="flex items-center gap-2 flex-1">
                            <input
                              type="text"
                              value={renamedCategoryInput}
                              onChange={(e) => setRenamedCategoryInput(e.target.value)}
                              className="flex-1 text-xs p-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#03478E]"
                            />
                            <button
                              type="button"
                              onClick={() => handleRenameCategory(cat, renamedCategoryInput)}
                              className="px-3 py-1 bg-[#03478E] text-white text-xs font-bold rounded-lg"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingCategoryName(null)}
                              className="px-2 py-1 text-gray-500 hover:text-gray-700 text-xs font-bold"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-gray-900">{cat}</span>
                              <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                {count} {count === 1 ? 'member' : 'members'}
                              </span>
                            </div>

                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingCategoryName(cat);
                                  setRenamedCategoryInput(cat);
                                }}
                                className="text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                Rename
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Close Button */}
              <div className="pt-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => setIsCategoryModalOpen(false)}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ======================= EDIT / ADD MEMBER MODAL ======================= */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl overflow-hidden border border-gray-100 my-8">
            <div className="bg-[#03478E] text-white px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-serif font-bold text-lg">
                  {modalMode === 'edit' ? `Edit: ${form.name || 'Member'}` : 'Add New Editorial Board Member'}
                </h3>
                <p className="text-xs text-blue-100">
                  {modalMode === 'edit' ? 'Update profile information and category' : 'Add a new scholar and assign their category'}
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 text-sm"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSaveModal} className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
              
              {/* Live Preview Avatar */}
              <div className="flex items-center gap-4 bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white shadow-xs bg-blue-100 flex-shrink-0 flex items-center justify-center">
                  {form.img ? (
                    <img
                      src={form.img}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/assets/img/default-doctor.svg';
                      }}
                    />
                  ) : (
                    <FaImage className="text-gray-400 text-xl" />
                  )}
                </div>
                <div className="text-xs min-w-0 flex-1">
                  <div className="font-bold text-gray-900 truncate">
                    {form.name || 'Member Name'}
                  </div>
                  <div className="text-blue-700 font-semibold truncate">
                    {form.designation || 'Board Category'}
                  </div>
                  {form.memberDesignation && (
                    <div className="text-gray-800 font-medium truncate flex items-center gap-1.5">
                      <FaBriefcase className="text-[#03478E] text-[10px] flex-shrink-0" />
                      <span>{form.memberDesignation}</span>
                    </div>
                  )}
                  {(form.address || form.institution) && (
                    <div className="text-gray-600 truncate flex items-center gap-1.5">
                      <FaMapMarkerAlt className="text-red-500 text-[10px] flex-shrink-0" />
                      <span>{form.address || form.institution}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Form Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                {/* Full Name */}
                <div className="space-y-1 md:col-span-2">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Dr. John Doe, MD"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Category / Designation */}
                <div className="space-y-1.5 md:col-span-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                      Board Category / Role *
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowInlineCatCreator(!showInlineCatCreator)}
                      className="text-xs text-[#03478E] font-bold hover:underline"
                    >
                      {showInlineCatCreator ? 'Hide' : '+ Add New Category'}
                    </button>
                  </div>

                  {/* Category Dropdown Select */}
                  <select
                    value={form.designation}
                    onChange={(e) => {
                      if (e.target.value === '__NEW__') {
                        setShowInlineCatCreator(true);
                      } else {
                        setForm({ ...form, designation: e.target.value });
                      }
                    }}
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  >
                    {allCategories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                    <option value="__NEW__">+ Create New Category...</option>
                  </select>

                  {/* Inline Category Creator */}
                  {showInlineCatCreator && (
                    <div className="p-3 bg-blue-50/70 border border-blue-200 rounded-xl space-y-2">
                      <p className="text-[11px] font-semibold text-blue-900">
                        Type custom category name (e.g. "International Advisory Board"):
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Category name..."
                          value={inlineCatInput}
                          onChange={(e) => setInlineCatInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleInlineAddCategory(inlineCatInput);
                            }
                          }}
                          className="flex-1 text-xs p-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-[#03478E]"
                        />
                        <button
                          type="button"
                          onClick={() => handleInlineAddCategory(inlineCatInput)}
                          className="px-3 py-1.5 bg-[#03478E] text-white text-xs font-bold rounded-lg hover:bg-[#023366]"
                        >
                          Set Category
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Suggestion Chips */}
                  <div className="flex flex-wrap items-center gap-1.5 pt-1">
                    <span className="text-[10px] text-gray-400 font-semibold mr-1">Quick Select:</span>
                    {allCategories.map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setForm({ ...form, designation: preset })}
                        className={`text-[10px] px-2.5 py-1 rounded-full border transition ${
                          form.designation === preset
                            ? 'bg-blue-100 border-blue-300 text-blue-900 font-bold'
                            : 'bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 1. Designation (Professional Designation / Post) */}
                <div className="space-y-1 md:col-span-2">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider flex items-center justify-between">
                    <span>1. Designation *</span>
                    <span className="text-[10px] text-gray-400 font-normal lowercase">e.g. Professor & HOD, Consultant</span>
                  </label>
                  <input
                    type="text"
                    value={form.memberDesignation}
                    onChange={(e) => setForm({ ...form, memberDesignation: e.target.value })}
                    placeholder="e.g. Professor & Head of Department, Associate Professor, Consultant"
                    className="w-full text-xs sm:text-sm p-3 border border-blue-200 bg-blue-50/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* 2. Address */}
                <div className="space-y-1 md:col-span-2">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider flex items-center justify-between">
                    <span>2. Institutional Address *</span>
                    <span className="text-[10px] text-gray-400 font-normal lowercase">e.g. Department, City, State, Country</span>
                  </label>
                  <input
                    type="text"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value, institution: form.institution || e.target.value })}
                    placeholder="e.g. AIM Homoeopathic Medical College Campus, Khatima, Udham Singh Nagar - 262308, Uttarakhand, India"
                    className="w-full text-xs sm:text-sm p-3 border border-blue-200 bg-blue-50/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Qualification */}
                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Academic Qualifications
                  </label>
                  <input
                    type="text"
                    value={form.qualification}
                    onChange={(e) => setForm({ ...form, qualification: e.target.value })}
                    placeholder="e.g. BHMS, MD (Hom.), PhD"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Priority / Sequence Number */}
                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Display Priority Number
                  </label>
                  <input
                    type="text"
                    value={form.sequenceNumber}
                    onChange={(e) => setForm({ ...form, sequenceNumber: e.target.value })}
                    placeholder="1, 2, 3..."
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Institution */}
                <div className="space-y-1 md:col-span-2">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Address
                  </label>
                  <input
                    type="text"
                    value={form.institution}
                    onChange={(e) => setForm({ ...form, institution: e.target.value })}
                    placeholder="e.g. AIM Homoeopathic Medical College & Hospital, India"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Email */}
                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Official Email
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="editor@example.com"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Contact Phone / Mobile
                  </label>
                  <input
                    type="text"
                    value={form.contactNumber}
                    onChange={(e) => setForm({ ...form, contactNumber: e.target.value })}
                    placeholder="+91 99999 99999"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Profile Link */}
                <div className="space-y-1 md:col-span-2">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Profile Link
                  </label>
                  <input
                    type="text"
                    value={form.profileLink}
                    onChange={(e) => setForm({ ...form, profileLink: e.target.value })}
                    placeholder="https://... (member's profile page, e.g. college or ORCID profile)"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                {/* Photo: URL or upload */}
                <div className="md:col-span-2">
                  <ImageUploadField
                    label="Profile Photo"
                    value={form.img}
                    onChange={(url) => setForm((prev) => ({ ...prev, img: url }))}
                    placeholder="https://.../photo.jpg or upload"
                  />
                </div>

              </div>

              {/* Social Links */}
              <div className="border-t border-gray-100 pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Scholarly / Social Links (Optional)
                  </label>
                  <button
                    type="button"
                    onClick={addSocial}
                    className="text-xs text-[#03478E] hover:underline font-bold"
                  >
                    + Add Link
                  </button>
                </div>

                {form.social.map((s, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Platform / Icon (e.g. LinkedIn, ORCID)"
                      value={s.icon}
                      onChange={(e) => handleSocialChange(idx, 'icon', e.target.value)}
                      className="w-1/3 text-xs p-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                    />
                    <input
                      type="url"
                      placeholder="https://orcid.org/..."
                      value={s.url}
                      onChange={(e) => handleSocialChange(idx, 'url', e.target.value)}
                      className="flex-1 text-xs p-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                    />
                    <button
                      type="button"
                      onClick={() => removeSocial(idx)}
                      className="text-red-500 hover:text-red-700 p-2 text-xs font-bold"
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>

              {/* Modal Actions */}
              <div className="border-t border-gray-100 pt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={saving}
                  className="px-5 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl text-xs font-bold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2.5 bg-[#03478E] hover:bg-[#023366] text-white rounded-xl text-xs font-bold shadow-md transition disabled:opacity-50"
                >
                  {saving ? 'Saving...' : modalMode === 'edit' ? 'Save Changes' : 'Add Member to Board'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ExecutiveEditorsAdmin;